import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

const useIsDark = () => {
    const { theme } = useTheme();
    return theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
};

/** Einheitliche Klassen für alle Seiten (Karten, Überschriften, Abstände). Breite kommt vom App-Container. */
export const pageStyles = {
    /** Innerer Abstand zwischen Blöcken + Stagger-Animation + Safe Area unten (Mobile). */
    container: 'space-y-6 sm:space-y-8 pb-10 sm:pb-16 mobile-safe-bottom page-stagger overflow-x-hidden min-w-0 animate-fade-in-up',
    /** Seiten-Header-Karte (jetzt transparent für Tabler pretitle/title). */
    headerCard: (isDark: boolean) =>
        `mb-8`,
    title: (isDark: boolean) => `font-heading text-2xl md:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`,
    subtitle: (isDark: boolean) => `text-[10px] font-black tracking-[0.2em] uppercase ${isDark ? 'text-white/60' : 'text-slate-500'} mb-1.5 leading-tight`,
    /** Inhalts-Karte (Listen, Formulare, Tabellen) – Glasoptik. */
    card: (isDark: boolean) =>
        `rounded-2xl overflow-hidden app-glass-panel transition-all`,
    /** Karten-Header (z. B. Sektionstitel in Karte). */
    cardHeader: (isDark: boolean) =>
        `px-6 sm:px-8 py-4 flex items-center gap-4 border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-white/50 bg-white/30'} backdrop-blur-sm`,
    /** Karten-Body. */
    cardBody: 'p-6 sm:p-8',
    /** Leerer Zustand (zentriert, dezent). */
    empty: (isDark: boolean) =>
        `p-12 text-center ${isDark ? 'text-white/50' : 'text-slate-400'} font-medium italic`,
};

type PageLayoutProps = {
    children: React.ReactNode;
};

/** Einheitlicher Seiten-Container für alle Ansichten (Abstand, Animation). */
export function PageLayout({ children }: PageLayoutProps) {
    return <div className={pageStyles.container}>{children}</div>;
}

type PageHeaderProps = {
    title: string;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    children?: React.ReactNode;
};

/** Einheitlicher Seitenkopf: Karte mit Titel, optional Untertitel und Aktionen. */
export function PageHeader({ title, subtitle, actions, children }: PageHeaderProps) {
    const isDark = useIsDark();
    return (
        <div className={pageStyles.headerCard(isDark)}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    {subtitle != null && (typeof subtitle === 'string' ? <p className={pageStyles.subtitle(isDark)}>{subtitle}</p> : <div className={pageStyles.subtitle(isDark)}>{subtitle}</div>)}
                    <h1 className={pageStyles.title(isDark)}>{title}</h1>
                    {children}
                </div>
                {actions != null && <div className="flex items-center gap-3 flex-shrink-0">{actions}</div>}
            </div>
        </div>
    );
}

type PageCardProps = {
    children: React.ReactNode;
    className?: string;
};

/** Einheitliche Inhalts-Karte. */
export function PageCard({ children, className = '' }: PageCardProps) {
    const isDark = useIsDark();
    return (
        <div className={`${pageStyles.card(isDark)} ${className}`}>
            {children}
        </div>
    );
}

/** Karten-Sektion mit optionalem Titel-Balken (für Listen/Tabellen). */
export function PageCardSection({
    title,
    icon: Icon,
    children,
}: {
    title?: string;
    icon?: React.ComponentType<{ size?: number; className?: string }>;
    children: React.ReactNode;
}) {
    const isDark = useIsDark();
    return (
        <div className={pageStyles.card(isDark)}>
            {title != null && (
                <div className={pageStyles.cardHeader(isDark)}>
                    {Icon != null && <Icon size={20} className={isDark ? 'text-slate-400' : 'text-slate-500'} />}
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>{title}</span>
                </div>
            )}
            <div className={pageStyles.cardBody}>
                {children}
            </div>
        </div>
    );
}

export { useIsDark };
